/**
 * Image Export Service using Queue System
 * Handles JPG, PNG, TIF exports through the queue system for scalability
 */

class ImageExportService {
  constructor() {
    this.baseUrl = (process.env.REACT_APP_API_BASE || '') + '/api/v1';
    this.activeJobs = new Map(); // jobId -> job data
  }

  isApiSuccess(data) {
    return Boolean(data?.success || (typeof data?.status === 'string' && data.status.toLowerCase() === 'success'));
  }

  /**
   * Start image export by adding to queue
   * @param {Object} template_content - The template content to render
   * @param {string} user_id - User ID
   * @param {string} format - Image format (jpg, png, tif)
   * @param {string} template_id - Template ID for identification (optional)
   * @param {string} sample_type - Sample type (default: sample)
   * @param {number} currentPageIndex - Current page index (optional, null for full export)
   * @param {string} colorMode - Color mode (RGB/CMYK) (optional)
   * @returns {Promise<{success: boolean, queueId: number}>}
   */
  async startImageExport(template_content, user_id = 'default-user', format = 'png', template_id = null, sample_type = 'sample', currentPageIndex = null, colorMode = 'RGB', customer_id = null, employee_id = null) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for job creation
      
      const response = await fetch(`${this.baseUrl}/queue/image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user_id,
          template_content: template_content,
          format: format,
          template_id: template_id,
          sample_type: sample_type,
          current_page_index: currentPageIndex,
          color_mode: colorMode,
          customer_id: customer_id,
          employee_id: employee_id
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (this.isApiSuccess(data)) {
        // Store job info
        this.activeJobs.set(data.queue_id, {
          id: data.queue_id,
          status: 'pending',
          progress: 0,
          format: format.toUpperCase(),
          currentPageIndex,
          colorMode,
          startTime: Date.now()
        });

        return {
          success: true,
          queueId: data.queue_id,
          message: `Started ${format.toUpperCase()} export`
        };
      } else {
        throw new Error(data.message || 'Failed to start export');
      }
    } catch (error) {
      console.error('Error starting image export:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get job status by queue ID
   * @param {number} queueId - Queue job ID
   * @returns {Promise<Object>} Job status
   */
  async getJobStatus(queueId) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for individual requests
      
      const response = await fetch(`${this.baseUrl}/queue/${queueId}`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (this.isApiSuccess(data)) {
        // Update local job data
        const localJob = this.activeJobs.get(queueId);
        if (localJob) {
          localJob.status = data.job.status;
          localJob.progress = data.job.progress || 0;
          localJob.progressText = data.job.progress_text;
          localJob.errorMessage = data.job.error_message;
          localJob.imageUrl = data.job.image_url;
          localJob.image_url = data.job.image_url; // For preview
          localJob.final_url = data.job.image_url; // For preview
          localJob.completedAt = data.job.completed_at;
        }

        return {
          success: true,
          job: data.job
        };
      } else {
        throw new Error(data.message || 'Failed to get job status');
      }
    } catch (error) {
      console.error('Error getting job status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get all user's queue jobs
   * @param {string} userId - User ID
   * @param {number} limit - Number of jobs to retrieve
   * @returns {Promise<Object>} User's queue jobs
   */
  async getUserQueueJobs(userId, limit = 50) {
    try {
      const response = await fetch(`${this.baseUrl}/queue/user/${userId}?limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting user queue jobs:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get queue jobs by template ID
   * @param {string} templateId - Template ID
   * @param {string} sampleType - Sample type filter (optional)
   * @param {number} limit - Number of jobs to retrieve
   * @returns {Promise<Object>} Template's queue jobs
   */
  async getTemplateQueueJobs(templateId, sampleType = null, limit = 50) {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      if (sampleType) params.append('sample_type', sampleType);

      const response = await fetch(`${this.baseUrl}/queue/template/${templateId}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting template queue jobs:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get per-image AI generation records for a template (one row per prompted
   * image item — the actual jobs sent to the Workflow AI Image Queue).
   * @param {string} templateId
   * @param {boolean} refresh - reconcile outstanding jobs against workflow first
   * @returns {Promise<Object>} { status, jobs: [...], total_count } or { success:false, error }
   */
  async getTemplateAiJobs(templateId, refresh = false) {
    try {
      const params = new URLSearchParams();
      if (refresh) params.append('refresh', 'true');
      const qs = params.toString() ? `?${params}` : '';
      const response = await fetch(`${this.baseUrl}/queue/template/${templateId}/ai-jobs${qs}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting template AI jobs:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Clear (delete) all queue jobs for a user. Optionally scope to a template.
   * @param {string} userId
   * @param {string|null} templateId
   * @returns {Promise<Object>} { success, deleted } or { success:false, error }
   */
  async clearUserJobs(userId, templateId = null) {
    try {
      const params = templateId ? `?template_id=${encodeURIComponent(templateId)}` : '';
      const response = await fetch(`${this.baseUrl}/queue/user/${encodeURIComponent(userId)}${params}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error clearing user queue jobs:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Cancel a queue job
   * @param {number} queueId - Queue job ID
   * @returns {Promise<Object>} Cancellation result
   */
  async cancelJob(queueId) {
    try {
      const response = await fetch(`${this.baseUrl}/queue/${queueId}/cancel`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (this.isApiSuccess(data)) {
        // Remove from local tracking
        this.activeJobs.delete(queueId);
      }

      return data;
    } catch (error) {
      console.error('Error canceling job:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Download completed image
   * @param {string} imageUrl - Image URL from completed job
   * @param {string} filename - Filename for download
   */
  downloadImage(imageUrl, filename) {
    try {
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  }

  /**
   * Generate filename for export
   * @param {string} format - Export format
   * @param {number} currentPageIndex - Current page index
   * @param {string} colorMode - Color mode
   * @returns {string} Generated filename
   */
  generateFilename(format, currentPageIndex, colorMode) {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const pageSuffix = currentPageIndex !== null ? `_page_${currentPageIndex + 1}` : '_full';
    const colorSuffix = colorMode && colorMode !== 'RGB' ? `_${colorMode}` : '';
    return `export${pageSuffix}${colorSuffix}_${timestamp}.${format.toLowerCase()}`;
  }

  /**
   * Get active jobs
   * @returns {Map} Active jobs map
   */
  getActiveJobs() {
    return this.activeJobs;
  }

  /**
   * Clear completed jobs from memory
   */
  clearCompletedJobs() {
    for (const [jobId, job] of this.activeJobs.entries()) {
      if (job.status === 'completed' || job.status === 'failed') {
        this.activeJobs.delete(jobId);
      }
    }
  }
}

// Create singleton instance
const imageExportService = new ImageExportService();
export default imageExportService;
